import React, { useCallback, useEffect, useState } from 'react';
import { Lightbulb, X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Top-of-page "how to use this" guide card. Shows a short title + 2-4
 * how-to bullets so a new rep (or Namratha after a quiet week) can land
 * on a page and know what they're supposed to do here.
 *
 * Dismissible per page-id via localStorage. When dismissed, this component
 * renders nothing - the PageHeader renders a small "Show guide" chip beside
 * the title via `guideId` prop. Both halves stay in sync via a window
 * 'mm-guide-changed' event so clicking the chip immediately reopens the card.
 *
 * Usage:
 *   <PageHeader title="Leads" guideId="leads-pipeline" ... />
 *   <PageGuide id="leads-pipeline" title="..." items={[...]} />
 */
const STORAGE_PREFIX = 'mm-page-guide-dismissed:';
const EVENT_NAME = 'mm-guide-changed';

/**
 * Shared state hook for guide-by-id. Two consumers (PageGuide card +
 * PageHeader chip) read/write the same localStorage key and sync via a
 * custom window event so each toggle is reflected immediately in both.
 *
 * Returns [collapsed, setCollapsed]. `collapsed` is `null` until the
 * effect reads localStorage on mount (prevents the open->closed flicker
 * on first paint).
 */
// Closed until asked for. A five-bullet card above every page's content was
// the loudest thing on it; the chip beside the title keeps it one click away
// and remembers a reader who opened it ('0') as well as one who closed it.
export function useGuideState(id, defaultOpen = false) {
  const key = STORAGE_PREFIX + id;
  const [collapsed, setCollapsed] = useState(null);

  useEffect(() => {
    try {
      const v = localStorage.getItem(key);
      setCollapsed(v === '1' ? true : v === '0' ? false : !defaultOpen);
    } catch {
      setCollapsed(!defaultOpen);
    }
    const handler = (e) => {
      if (e.detail?.id === id) setCollapsed(e.detail.collapsed);
    };
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, [key, id, defaultOpen]);

  const update = useCallback((next) => {
    try {
      localStorage.setItem(key, next ? '1' : '0');
    } catch {}
    setCollapsed(next);
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { id, collapsed: next } }));
  }, [key, id]);

  return [collapsed, update];
}

/**
 * Guide toggle chip rendered by PageHeader beside the title. Always visible
 * (so reps see at a glance that this page has a how-to), and clicking it
 * toggles the in-page PageGuide card. When the guide is open the chip reads
 * 'Hide guide'; when collapsed it reads 'Show guide'. Both states emit the
 * same shared event the card listens for, so click anywhere -> instant sync.
 */
export function ShowGuideChip({ id, className }) {
  const [collapsed, setCollapsed] = useGuideState(id);
  if (collapsed === null) return null; // pre-hydration
  const isShowing = collapsed === false;
  return (
    <button
      type="button"
      onClick={() => setCollapsed(!collapsed)}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[13px] transition-colors',
        isShowing
          ? 'border-primary/25 bg-primary-soft/60 text-primary hover:bg-primary-soft'
          : 'border-border bg-muted/40 text-muted-foreground hover:bg-muted hover:text-foreground',
        className,
      )}
    >
      <Lightbulb className="size-3" />
      {isShowing ? 'Hide tips' : 'Tips'}
    </button>
  );
}

export function PageGuide({ id, title, items, defaultOpen = false, className }) {
  const [collapsed, setCollapsed] = useGuideState(id, defaultOpen);

  // Don't render anything while we hydrate localStorage, or when dismissed.
  // The collapsed-state chip is rendered by PageHeader beside the title.
  if (collapsed === null || collapsed === true) return null;

  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-md border border-primary/15 bg-primary-soft/30 px-4 py-3',
        className,
      )}
    >
      <Lightbulb className="mt-0.5 size-4 shrink-0 text-primary" />
      <div className="min-w-0 flex-1">
        {title && (
          <p className="mb-1 text-[13px] font-semibold text-foreground">{title}</p>
        )}
        {Array.isArray(items) ? (
          <ul className="space-y-0.5 text-xs leading-relaxed text-foreground/90">
            {items.map((it, i) => (
              <li key={i} className="flex gap-1.5">
                <span className="shrink-0 text-muted-foreground">·</span>
                <span>{it}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs leading-relaxed text-foreground/90">{items}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => setCollapsed(true)}
        aria-label="Dismiss guide"
        className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-background hover:text-foreground"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}
